import { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing, Send, AlertTriangle, Smartphone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { PageHeader } from '../../components/Shared';
import api from '../../services/api';
import usePushNotifications from '../../hooks/usePushNotifications';

const NotificationSettings = () => {
  const {
    supported,
    permission,
    subscribed,
    busy,
    error,
    subscribe,
    unsubscribe,
  } = usePushNotifications();

  const [status, setStatus] = useState({ configured: false, activeSubscriptions: 0 });
  const [sendingTest, setSendingTest] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/push/status');
      setStatus(data);
    } catch (_e) { /* non-fatal */ }
  };

  useEffect(() => { loadStatus(); }, [subscribed]);

  const handleToggle = async (next) => {
    if (next) {
      const ok = await subscribe();
      if (ok) toast.success('Notifications enabled — you\'re all set');
    } else {
      await unsubscribe();
      toast.success('Notifications disabled on this device');
    }
    loadStatus();
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post('/push/test');
      if (data?.sent > 0) {
        toast.success(`Test sent to ${data.sent} device${data.sent === 1 ? '' : 's'} — check your home screen`);
      } else {
        toast.error('No active devices — enable notifications above first');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send test push');
    } finally {
      setSendingTest(false);
    }
  };

  // Permission state -> friendly summary
  const permLabel = permission === 'granted' ? 'Allowed' :
                    permission === 'denied' ? 'Blocked at device level' :
                    permission === 'unsupported' ? 'Not supported on this device' :
                    'Not yet asked';
  const permTone = permission === 'granted' ? 'text-emerald-700' :
                   permission === 'denied' ? 'text-red-700' :
                   'text-slate-500';

  return (
    <div className="space-y-6 max-w-3xl" data-testid="notification-settings-page">
      <PageHeader title="Notifications" description="Push alerts to your home-screen icon" />

      {!supported && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-4 py-3 text-sm flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Push isn't available on this browser.</div>
            <div className="text-xs mt-0.5 text-amber-800">Open the Lutrom Portal in Safari (iPhone/iPad) or Chrome/Edge/Firefox (desktop), add it to your home screen, then come back here.</div>
          </div>
        </div>
      )}

      {!status.configured && (
        <div className="bg-red-50 border border-red-200 text-red-900 rounded-md px-4 py-3 text-sm flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>Push isn't configured on the server. Set <span className="font-mono text-xs">VAPID_PRIVATE_KEY</span>, <span className="font-mono text-xs">VAPID_PUBLIC_KEY</span> and <span className="font-mono text-xs">VAPID_SUBJECT</span> in <span className="font-mono text-xs">backend/.env</span>.</div>
        </div>
      )}

      {/* Master toggle */}
      <div
        className="bg-white border border-slate-200 rounded-md p-4 sm:p-5 flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row"
        data-testid="push-toggle-row"
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={`h-10 w-10 rounded flex items-center justify-center shrink-0 ${subscribed ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
            {subscribed ? <BellRing className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display uppercase tracking-wide text-sm text-slate-700">
              Notifications on this device
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              You'll get a push notification on your home-screen Lutrom icon for events that matter to your role — new work requests, parsed bills, work-order updates and more.
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px]">
              <span className={`inline-flex items-center gap-1 ${permTone}`}>
                <Smartphone className="h-3 w-3" /> Browser permission: <strong className="font-medium">{permLabel}</strong>
              </span>
              <span className="inline-flex items-center gap-1 text-slate-500">
                <Bell className="h-3 w-3" /> Active devices for you: <strong className="text-slate-700 font-medium">{status.activeSubscriptions}</strong>
              </span>
            </div>
            {error && <div className="text-xs text-red-700 mt-2">{error}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
          <span className={`text-xs font-medium ${subscribed ? 'text-emerald-700' : 'text-slate-400'}`}>
            {subscribed ? 'On' : 'Off'}
          </span>
          <Switch
            checked={subscribed}
            onCheckedChange={handleToggle}
            disabled={busy || !supported || !status.configured}
            data-testid="push-toggle"
            aria-label="Enable notifications"
          />
        </div>
      </div>

      {/* Test push */}
      <div className="bg-white border border-slate-200 rounded-md p-4 sm:p-5">
        <div className="font-display uppercase tracking-wide text-sm text-slate-700 mb-1">Test the wiring</div>
        <div className="text-xs text-slate-500 mb-4">
          Sends a notification to every device currently subscribed under your account — useful right after you add the portal to your home screen on a new phone.
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={sendTest}
          disabled={sendingTest || !subscribed}
          data-testid="send-test-push-btn"
        >
          <Send className={`h-4 w-4 mr-2 ${sendingTest ? 'animate-pulse' : ''}`} />
          {sendingTest ? 'Sending…' : 'Send test push'}
        </Button>
      </div>

      {/* Audience explainer */}
      <div className="bg-slate-50 border border-slate-200 rounded-md p-4 sm:p-5">
        <div className="font-display uppercase tracking-wide text-sm text-slate-700 mb-2">What you'll get pinged about</div>
        <ul className="text-xs text-slate-600 space-y-1 list-disc pl-5">
          <li><strong>Admin</strong> — every event: new work requests, bills parsed / failed, work-order updates, quotes accepted.</li>
          <li><strong>Employee</strong> — only when a work order is assigned to you or something is added to your schedule.</li>
          <li><strong>Customer</strong> — only when their work order is completed.</li>
        </ul>
      </div>
    </div>
  );
};

export default NotificationSettings;
